import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, can } from '../lib/auth';

/**
 * Contractor Master — the pasting contractors on the monthly bill.
 *
 * Before this page the contractor was a free-text column on each production
 * entry.  Two typos produced two contractors on the bill and the only way
 * to remove a contractor from new entries was to rename them in every
 * historical row.  The master makes it a two-step list: add, deactivate.
 *
 * Super Admin only: the route, the sidebar link and every API call need
 * EDIT_CONTRACTORS, which no other role holds.
 */

interface Contractor {
  id: number;
  name: string;
  code: string | null;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  defaultHourlyRate: number | null;
  defaultProdRate: number | null;
  isActive: boolean;
  notWorkingReason: string | null;
  notWorkingFrom: string | null;
  isDeleted: boolean;
}

export function ContractorsPage() {
  const { user } = useAuth();
  const canEdit = can(user, 'EDIT_CONTRACTORS');
  const qc = useQueryClient();

  const [filter, setFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Partial<Contractor> | null>(null);
  const [deactivating, setDeactivating] = useState<Contractor | null>(null);

  // Full list only for anyone who can see inactive rows (EDIT_CONTRACTORS);
  // everyone else gets the active-only endpoint, so a non-admin cannot
  // discover who was deactivated.
  const list = useQuery({
    queryKey: ['contractors', canEdit ? 'all' : 'active'],
    queryFn: () => api<{ rows: Contractor[] }>(
      canEdit ? '/api/contractors/all' : '/api/contractors',
    ),
  });

  const rows = useMemo(() => {
    const all = list.data?.rows ?? [];
    const s = search.trim().toLowerCase();
    return all.filter((r) => {
      if (filter === 'active' && !r.isActive) return false;
      if (filter === 'inactive' && r.isActive) return false;
      if (s && !r.name.toLowerCase().includes(s)
          && !(r.code ?? '').toLowerCase().includes(s)
          && !(r.contactPerson ?? '').toLowerCase().includes(s)) {
        return false;
      }
      return true;
    });
  }, [list.data, filter, search]);

  const save = useMutation({
    mutationFn: (c: Partial<Contractor>) =>
      c.id
        ? api(`/api/contractors/${c.id}`, { method: 'PUT', body: JSON.stringify(c) })
        : api('/api/contractors', { method: 'POST', body: JSON.stringify(c) }),
    onSuccess: () => {
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['contractors'] });
      qc.invalidateQueries({ queryKey: ['production-contractors'] });
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: number) =>
      api(`/api/contractors/${id}/reactivate`, { method: 'PATCH', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contractors'] });
      qc.invalidateQueries({ queryKey: ['production-contractors'] });
    },
  });

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Contractor Master</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            The pasting contractors paid by the monthly bill.
            {canEdit ? ' Deactivate when a contractor stops working — the dropdown drops them, the history keeps them.' : ''}
          </p>
        </div>
        {canEdit && (
          <button
            className="px-3 py-1.5 rounded bg-brand text-white text-sm font-medium shadow-sm hover:bg-brand/90"
            onClick={() => setEditing({ isActive: true })}
          >+ Add contractor</button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input
          className="input w-full md:w-72"
          placeholder="Search by name, code or contact…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {canEdit && (
          <div className="inline-flex rounded border border-slate-200 bg-white text-xs">
            {(['active', 'inactive', 'all'] as const).map((k) => (
              <button
                key={k}
                className={`px-3 py-1.5 ${filter === k ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                onClick={() => setFilter(k)}
              >{k === 'all' ? 'All' : k.charAt(0).toUpperCase() + k.slice(1)}</button>
            ))}
          </div>
        )}
        <div className="ml-auto text-xs text-slate-500">{rows.length} shown</div>
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Name</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Code</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Contact</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Phone</th>
              <th className="px-3 py-2 text-center font-medium text-slate-600">Active</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400">No contractors.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-1.5">
                  <div className="font-medium text-slate-800">{r.name}</div>
                  {!r.isActive && (
                    <div className="text-[11px] text-slate-500">
                      Not working since {r.notWorkingFrom ?? '—'}
                      {r.notWorkingReason ? ` • ${r.notWorkingReason}` : ''}
                    </div>
                  )}
                </td>
                <td className="px-3 py-1.5 text-slate-600">{r.code ?? '—'}</td>
                <td className="px-3 py-1.5">{r.contactPerson ?? '—'}</td>
                <td className="px-3 py-1.5">{r.phone ?? '—'}</td>
                <td className="px-3 py-1.5 text-center">
                  {r.isActive
                    ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[11px] font-medium">Active</span>
                    : <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[11px] font-medium">Inactive</span>}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  {canEdit && (
                    <>
                      <button
                        className="text-brand hover:underline text-xs mr-3"
                        onClick={() => setEditing(r)}
                      >Edit</button>
                      {r.isActive ? (
                        <button
                          className="text-amber-700 hover:underline text-xs"
                          onClick={() => setDeactivating(r)}
                        >Deactivate</button>
                      ) : (
                        <button
                          className="text-emerald-700 hover:underline text-xs"
                          onClick={() => reactivate.mutate(r.id)}
                        >Reactivate</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ContractorEditor
          value={editing}
          onCancel={() => setEditing(null)}
          onSave={(c) => save.mutate(c)}
          saving={save.isPending}
          error={(save.error as Error | null)?.message}
        />
      )}
      {deactivating && (
        <DeactivateDialog
          contractor={deactivating}
          onCancel={() => setDeactivating(null)}
          onSaved={() => {
            setDeactivating(null);
            qc.invalidateQueries({ queryKey: ['contractors'] });
            qc.invalidateQueries({ queryKey: ['production-contractors'] });
          }}
        />
      )}
    </div>
  );
}

function ContractorEditor({
  value, onCancel, onSave, saving, error,
}: {
  value: Partial<Contractor>;
  onCancel: () => void;
  onSave: (c: Partial<Contractor>) => void;
  saving: boolean;
  error?: string | undefined;
}) {
  const [form, setForm] = useState<Partial<Contractor>>(value);
  const update = (k: keyof Contractor, v: unknown) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">
          {form.id ? `Edit ${value.name}` : 'New contractor'}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label">Name *</label>
            <input className="input" value={form.name ?? ''}
                   onChange={(e) => update('name', e.target.value)} />
          </div>
          <div>
            <label className="label">Code</label>
            <input className="input" value={form.code ?? ''}
                   onChange={(e) => update('code', e.target.value)} />
          </div>
          <div>
            <label className="label">Contact person</label>
            <input className="input" value={form.contactPerson ?? ''}
                   onChange={(e) => update('contactPerson', e.target.value)} />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone ?? ''}
                   onChange={(e) => update('phone', e.target.value)} />
          </div>
          <div>
            <label className="label">Email</label>
            <input className="input" value={form.email ?? ''}
                   onChange={(e) => update('email', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label">Address</label>
            <textarea className="input min-h-16" rows={2} value={form.address ?? ''}
                      onChange={(e) => update('address', e.target.value)} />
          </div>
          <div>
            <label className="label">Default hourly rate (₹)</label>
            <input type="number" step="any" className="input"
                   value={form.defaultHourlyRate ?? ''}
                   onChange={(e) => update('defaultHourlyRate', e.target.value === '' ? null : Number(e.target.value))} />
          </div>
          <div>
            <label className="label">Default per-1000 rate (₹)</label>
            <input type="number" step="any" className="input"
                   value={form.defaultProdRate ?? ''}
                   onChange={(e) => update('defaultProdRate', e.target.value === '' ? null : Number(e.target.value))} />
          </div>
        </div>
        {error && <div className="text-red-700 text-sm mt-3">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn-primary"
                  disabled={saving || !(form.name ?? '').trim()}
                  onClick={() => onSave(form)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeactivateDialog({
  contractor, onCancel, onSaved,
}: {
  contractor: Contractor;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [reason, setReason] = useState('');
  const [fromDate, setFromDate] = useState(today);
  const run = useMutation({
    mutationFn: () =>
      api(`/api/contractors/${contractor.id}/deactivate`, {
        method: 'PATCH',
        body: JSON.stringify({ reason, fromDate }),
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4"
           onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-1">Deactivate {contractor.name}?</h2>
        <p className="text-xs text-slate-500 mb-3">
          Historical production, QC and dispatch records are unchanged.
          The contractor stops appearing on the Production Entry dropdown.
        </p>
        <label className="label">Reason *</label>
        <textarea className="input min-h-16" rows={2} value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. moved to another plant" />
        <label className="label mt-3">Not working from</label>
        <input type="date" className="input" value={fromDate}
               onChange={(e) => setFromDate(e.target.value)} />
        {run.error && <div className="text-red-700 text-sm mt-3">{(run.error as Error).message}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn-primary bg-amber-600 hover:bg-amber-700"
                  disabled={run.isPending || reason.trim().length < 3 || !fromDate}
                  onClick={() => run.mutate()}>
            {run.isPending ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}
